// 6章 コンクリート工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_STRENGTH = `
<svg viewBox="0 0 660 260" role="img" aria-label="コンクリート強度の関係。設計基準強度Fcに構造体強度補正値Sを加えたものが調合管理強度。レディーミクストコンクリートの呼び強度は調合管理強度以上とする">
  <g fill="currentColor" opacity="0.35">
    <rect x="60" y="120" width="300" height="60" rx="6"/>
  </g>
  <g fill="currentColor" opacity="0.7">
    <rect x="360" y="120" width="120" height="60" rx="6"/>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="2">
    <rect x="60" y="120" width="420" height="60" rx="6"/>
  </g>
  <g fill="currentColor" font-size="15" font-weight="bold" text-anchor="middle">
    <text x="210" y="155">設計基準強度 Fc</text>
    <text x="420" y="148">＋S</text>
  </g>
  <g fill="currentColor" font-size="12" text-anchor="middle" opacity="0.85">
    <text x="210" y="172">（構造計算で決まる強度）</text>
    <text x="420" y="168">補正値 3 or 6</text>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="60" y1="200" x2="480" y2="200"/>
    <line x1="60" y1="192" x2="60" y2="208"/>
    <line x1="480" y1="192" x2="480" y2="208"/>
  </g>
  <g fill="currentColor" font-size="14" font-weight="bold" text-anchor="middle">
    <text x="270" y="228">調合管理強度（＝工場に発注する強度の基準）</text>
  </g>
  <g fill="currentColor" font-size="12.5" opacity="0.85">
    <text x="60" y="40" font-weight="bold">Fc ＋ S ＝ 調合管理強度 ≦ 呼び強度</text>
    <text x="60" y="64">S（構造体強度補正値）は、セメント種類と打込み〜28日の予想平均気温で決まる：</text>
    <text x="60" y="86">普通ポルトランドの場合　8℃以上 → S=3　／　0〜8℃未満 → S=6（表6.3.2）</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.7">
    <text x="60" y="252">現場の供試体で確認する「構造体コンクリート強度」は Fc 以上が必要（6.2.2）</text>
  </g>
</svg>`;

const SVG_SLUMP = `
<svg viewBox="0 0 640 260" role="img" aria-label="スランプ試験。高さ30センチのスランプコーンにコンクリートを詰めて引き上げ、頂部からの下がり量をスランプ値とする。スランプ8から18センチの許容差はプラスマイナス2.5センチ">
  <g fill="none" stroke="currentColor" stroke-width="2">
    <path d="M120 60 L100 210 H220 L200 60 Z"/>
  </g>
  <g fill="currentColor" opacity="0.35">
    <path d="M330 120 C350 100 430 100 450 120 L470 210 H310 Z"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5" stroke-dasharray="5 4" opacity="0.8">
    <line x1="240" y1="60" x2="500" y2="60"/>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="390" y1="60" x2="390" y2="108"/>
    <line x1="382" y1="60" x2="398" y2="60"/>
    <line x1="382" y1="108" x2="398" y2="108"/>
  </g>
  <g stroke="currentColor" stroke-width="2" opacity="0.5">
    <line x1="60" y1="210" x2="580" y2="210"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="160" y="40">スランプコーン（高さ30cm）</text>
    <text x="470" y="88">スランプ値（下がり量）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="60" y="240">柱・梁・スラブ・壁は18cm、基礎・基礎梁・土間は15または18cm（特記がなければ）。許容差は±2.5cm（8〜18cm のとき）</text>
  </g>
</svg>`;

const SVG_TIME = `
<svg viewBox="0 0 640 220" role="img" aria-label="練混ぜから打込み終了までの時間制限。外気温25度以下なら120分以内、25度を超えると90分以内">
  <g stroke="currentColor" stroke-width="2">
    <line x1="60" y1="80" x2="580" y2="80"/>
    <line x1="60" y1="150" x2="580" y2="150"/>
  </g>
  <g fill="currentColor">
    <polygon points="580,80 566,74 566,86"/>
    <polygon points="580,150 566,144 566,156"/>
    <circle cx="60" cy="80" r="6"/>
    <circle cx="60" cy="150" r="6"/>
    <rect x="440" y="70" width="4" height="20"/>
    <rect x="345" y="140" width="4" height="20"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold">
    <text x="60" y="50">外気温 25℃以下 → 120分以内</text>
    <text x="60" y="122">外気温 25℃超（暑中） → 90分以内</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="56" y="103">練混ぜ（出荷）</text>
    <text x="415" y="107">120分</text>
    <text x="325" y="177">90分</text>
    <text x="470" y="177">＝打込み「終了」まで</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.75">
    <text x="60" y="205">納入書の出発時刻で管理する。冷却や凝結遅延の措置をすれば、承諾を受けて変更できる（6.6.2(2)）</text>
  </g>
</svg>`;

const SVG_POUR_ORDER = `
<svg viewBox="0 0 640 260" role="img" aria-label="打込みの順序。柱と壁に打ち込み、コンクリートの沈みが落ち着いてから梁へ、梁の沈みが落ち着いてからスラブへ打ち込む">
  <g fill="none" stroke="currentColor" stroke-width="2">
    <rect x="80" y="60" width="50" height="160"/>
    <rect x="510" y="60" width="50" height="160"/>
    <rect x="130" y="60" width="380" height="45"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5" opacity="0.6">
    <line x1="130" y1="60" x2="510" y2="60"/>
  </g>
  <g fill="currentColor" font-size="14" font-weight="bold" text-anchor="middle">
    <text x="105" y="150">①柱</text>
    <text x="535" y="150">①壁</text>
    <text x="320" y="90">③梁 → ④スラブ</text>
  </g>
  <g fill="currentColor" font-size="12.5" text-anchor="middle" opacity="0.85">
    <text x="320" y="135">②沈みが落ち着くのを待つ</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="60" y="245">柱・壁 →（沈み待ち）→ 梁 →（沈み待ち）→ スラブ。一気に打つと沈降ひび割れの原因になる（6.6.3(7)）</text>
  </g>
</svg>`;

const SVG_JOINT = `
<svg viewBox="0 0 640 260" role="img" aria-label="打継ぎの位置。梁とスラブはスパンの中央または端から4分の1付近に、柱と壁はスラブや基礎の上端に設ける">
  <g fill="none" stroke="currentColor" stroke-width="2">
    <rect x="100" y="40" width="44" height="180"/>
    <rect x="496" y="40" width="44" height="180"/>
    <rect x="144" y="40" width="352" height="40"/>
  </g>
  <g stroke="currentColor" stroke-width="3" stroke-dasharray="7 5">
    <line x1="232" y1="40" x2="232" y2="80"/>
    <line x1="320" y1="40" x2="320" y2="80"/>
    <line x1="122" y1="220" x2="144" y2="220"/>
  </g>
  <g stroke="currentColor" stroke-width="3" stroke-dasharray="7 5" opacity="0.9">
    <line x1="100" y1="220" x2="122" y2="220"/>
    <line x1="496" y1="220" x2="540" y2="220"/>
  </g>
  <g fill="currentColor" font-size="12.5" font-weight="bold">
    <text x="170" y="110">梁・スラブ：スパン中央 or 端から1/4付近</text>
    <text x="150" y="250">柱・壁：スラブ・壁梁・基礎の「上端」</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.75">
    <text x="360" y="140">打継ぎ面はレイタンスを除去し、</text>
    <text x="360" y="158">散水して湿潤にしてから打つ</text>
  </g>
</svg>`;

const SVG_VIBRATOR = `
<svg viewBox="0 0 640 260" role="img" aria-label="棒形振動機の使い方。ほぼ垂直に挿入して先端を下層に入れ、挿入間隔は60センチ以下、表面にペーストが浮くまで加振し、穴が残らないよう加振しながらゆっくり引き抜く">
  <g fill="currentColor" opacity="0.25">
    <rect x="40" y="150" width="560" height="70"/>
  </g>
  <g fill="currentColor" opacity="0.4">
    <rect x="40" y="90" width="560" height="60"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5" stroke-dasharray="4 4" opacity="0.8">
    <line x1="40" y1="150" x2="600" y2="150"/>
  </g>
  <g stroke="currentColor" stroke-width="8" stroke-linecap="round">
    <line x1="180" y1="40" x2="180" y2="175"/>
    <line x1="420" y1="40" x2="420" y2="175"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="180" y1="60" x2="420" y2="60"/>
    <line x1="180" y1="52" x2="180" y2="68"/>
    <line x1="420" y1="52" x2="420" y2="68"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="300" y="50">挿入間隔 60cm 以下</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="450" y="120">上層（打ち込んだ層）</text>
    <text x="450" y="200">下層（先端を10cmほど入れる）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="245">ほぼ垂直に挿入・表面にペーストが浮くまで加振・穴を残さないよう加振しながら徐々に引き抜く（6.6.5(3)）</text>
  </g>
</svg>`;

const SVG_CURING = `
<svg viewBox="0 0 640 230" role="img" aria-label="湿潤養生の期間。早強ポルトランドセメントは3日以上、普通ポルトランドセメントなどは5日以上、高炉セメントB種などは7日以上">
  <g fill="currentColor" opacity="0.45">
    <rect x="200" y="50" width="150" height="30" rx="4"/>
  </g>
  <g fill="currentColor" opacity="0.65">
    <rect x="200" y="100" width="250" height="30" rx="4"/>
  </g>
  <g fill="currentColor" opacity="0.85">
    <rect x="200" y="150" width="350" height="30" rx="4"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold">
    <text x="30" y="70">早強</text>
    <text x="30" y="120">普通・A種・エコ</text>
    <text x="30" y="170">B種・中庸熱・低熱</text>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold">
    <text x="360" y="70">3日以上</text>
    <text x="460" y="120">5日以上</text>
    <text x="560" y="170">7日以上</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="30" y="212">方法：せき板存置、養生マット・水密シート被覆、散水・噴霧、膜養生剤など。「乾かさない」が目的（6.7.2）</text>
  </g>
</svg>`;

const SVG_FORM_PERIOD = `
<svg viewBox="0 0 660 280" role="img" aria-label="型枠の最小存置期間の考え方。せき板は圧縮強度5ニュートン以上または材齢による。スラブ下の支柱は設計基準強度の85パーセント以上または12ニュートン以上かつ構造計算による安全確認。梁下の支柱は設計基準強度以上">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="30"  y="46" width="190" height="180" rx="10"/>
    <rect x="235" y="46" width="200" height="180" rx="10"/>
    <rect x="450" y="46" width="185" height="180" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13.5" font-weight="bold" text-anchor="middle">
    <text x="125" y="76">せき板</text>
    <text x="335" y="76">支柱（スラブ下）</text>
    <text x="542" y="76">支柱（梁下）</text>
  </g>
  <g fill="currentColor" font-size="12" text-anchor="middle" opacity="0.85">
    <text x="125" y="100">（基礎・梁側・柱・壁）</text>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="46" y="130">圧縮強度</text>
    <text x="46" y="150" font-weight="bold">5N/mm²以上</text>
    <text x="46" y="176">または材齢による</text>
    <text x="46" y="196" opacity="0.8">（普通・15℃以上で3日）</text>
    <text x="251" y="130" font-weight="bold">Fcの85％以上</text>
    <text x="251" y="150" font-weight="bold">又は12N/mm²以上</text>
    <text x="251" y="176">＋構造計算で</text>
    <text x="251" y="196">安全確認</text>
    <text x="466" y="130" font-weight="bold">Fc以上</text>
    <text x="466" y="156">＋構造計算で</text>
    <text x="466" y="176">安全確認</text>
    <text x="466" y="200" opacity="0.8">（材齢なら28日）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="30" y="256">スラブ下・梁下のせき板は「支柱を外した後」に外す。支柱の盛替えは行わない（6.8.4(4)）</text>
  </g>
</svg>`;

const SVG_STRENGTH_TEST = `
<svg viewBox="0 0 640 270" role="img" aria-label="調合管理強度の判定。1回の試験は供試体3個の平均。3回の試験で判定し、各回の平均が調合管理強度の85パーセント以上、かつ3回の総平均が調合管理強度以上なら合格">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="30"  y="40" width="170" height="70" rx="8"/>
    <rect x="30"  y="120" width="170" height="70" rx="8"/>
    <rect x="30"  y="200" width="170" height="55" rx="8"/>
    <rect x="300" y="90" width="310 " height="110" rx="10"/>
  </g>
  <g fill="currentColor">
    <circle cx="60" cy="75" r="9"/><circle cx="90" cy="75" r="9"/><circle cx="120" cy="75" r="9"/>
    <circle cx="60" cy="155" r="9"/><circle cx="90" cy="155" r="9"/><circle cx="120" cy="155" r="9"/>
    <circle cx="60" cy="228" r="9"/><circle cx="90" cy="228" r="9"/><circle cx="120" cy="228" r="9"/>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="140" y="80">1回目</text>
    <text x="140" y="160">2回目</text>
    <text x="140" y="233">3回目</text>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="205" y1="145" x2="290" y2="145"/>
  </g>
  <g fill="currentColor">
    <polygon points="300,145 288,139 288,151"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold">
    <text x="320" y="122">合格条件（両方満たす）</text>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="320" y="150">① 各回の平均 ≧ 調合管理強度の85％</text>
    <text x="320" y="176">② 3回の総平均 ≧ 調合管理強度</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="30" y="30">●＝供試体（1回の試験＝3個の平均、標準養生・材齢28日）</text>
  </g>
</svg>`;

const SVG_STRUCT_TEST = `
<svg viewBox="0 0 660 260" role="img" aria-label="構造体コンクリート強度の判定は3つのルートのいずれか。現場水中養生の28日強度、現場封かん養生の28日と91日以内の強度、標準養生の28日強度のどれかで判定する">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="24"  y="50" width="200" height="150" rx="10"/>
    <rect x="234" y="50" width="200" height="150" rx="10"/>
    <rect x="444" y="50" width="195" height="150" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="124" y="78">ルートA：現場水中養生</text>
    <text x="334" y="78">ルートB：現場封かん養生</text>
    <text x="541" y="78">ルートC：標準養生</text>
  </g>
  <g fill="currentColor" font-size="12">
    <text x="40" y="106">材齢28日で：</text>
    <text x="40" y="128">・平均気温20℃以上</text>
    <text x="52" y="148">→ 調合管理強度以上</text>
    <text x="40" y="170">・20℃未満</text>
    <text x="52" y="190">→ Fc＋3 以上</text>
    <text x="250" y="106">28日 ≧ 0.7×Fc</text>
    <text x="250" y="130">かつ</text>
    <text x="250" y="154">28日超91日以内</text>
    <text x="250" y="176">≧ Fc＋3</text>
    <text x="460" y="106">28日で</text>
    <text x="460" y="130">調合管理強度以上</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="24" y="234">どれか1つを満たせば合格。不合格ならコア採取等で確認し、必要な措置に監督職員の承諾を受ける（6.9.5）</text>
  </g>
</svg>`;

const ch06: Record<string, Commentary> = {
  "6.1.1": {
    plainSummary:
      "この章は工事現場で施工するコンクリート工事のルール。1章とセットで適用する。",
  },

  "6.1.2": {
    plainSummary:
      "コンクリート工事のゴールは3つ。①所定の材料 ②所定の形状・寸法・位置と密実な表面 ③所定の強度と、耐力・耐久性・耐火性に有害な欠陥がないこと。",
    why: "6章は材料・調合（2〜3節）、製造・運搬（4節）、品質管理（5節）、打込み（6節）、養生（7節）、型枠（8節）、試験（9節）と、この3つのゴールを実現する流れで構成されています。",
  },

  "6.2.1": {
    plainSummary:
      "コンクリートはⅠ類（JIS認証工場のJIS適合品）とⅡ類に分かれ、特記がなければⅠ類。普通コンクリートか軽量コンクリートかは特記による。",
    why: "Ⅰ類はJISマーク認証を受けた生コン工場の製品で、品質管理の信頼性が制度的に担保されています。だから試し練りの省略（6.3.2）など手続きが軽くなります。Ⅱ類はJIS適合でも認証外のため、追加の資料提出が必要です（6.4.1）。",
    glossary: [
      {
        term: "レディーミクストコンクリート（生コン）",
        meaning:
          "工場で練り混ぜてミキサー車で現場に運ぶコンクリート。JIS A 5308が品質規格。",
      },
    ],
  },

  "6.2.2": {
    plainSummary:
      "設計基準強度Fcは普通コンクリートで36N/mm²以下（特記による）。使用するコンクリートは材齢28日で調合管理強度以上、構造体コンクリート強度はFc以上が必要。",
    why: "強度には3つのレベルがあります。①Fc＝構造計算の前提となる強度 ②調合管理強度＝Fcに補正値Sを加えた、工場発注の基準 ③構造体コンクリート強度＝実際の建物の中のコンクリートの強度（現場採取の供試体で推定）。「現場のコンクリートは供試体より条件が悪い」ことを見込んで、あらかじめ上乗せ（S）して発注する仕組みです。",
    glossary: [
      {
        term: "設計基準強度（Fc）",
        meaning: "構造計算で基準とするコンクリートの圧縮強度。単位はN/mm²。",
      },
      {
        term: "材齢",
        meaning: "コンクリートを打ち込んでからの日数。強度は材齢28日が基準。",
      },
    ],
  },

  "6.2.3": {
    plainSummary:
      "普通コンクリートの気乾単位容積質量（乾いた状態の重さ）は2.1超〜2.5t/m³が標準。",
    why: "構造計算では躯体の自重を仮定しています。コンクリートが想定より重い/軽いと計算前提が崩れるため、範囲が決められています。",
  },

  "6.2.4": {
    plainSummary:
      "ワーカビリティー（施工のしやすさ）は打込み条件に応じ、密実に打ち込めて材料分離が少ないこと。スランプは特記がなければ柱・梁・スラブ・壁で18cm、基礎系で15または18cm。",
    why: "スランプはコンクリートの軟らかさの指標です。軟らかいほど施工は楽ですが、水が多い＝強度・耐久性に不利。だから「必要最小限の軟らかさ」を狙います。荷卸し地点での値で管理することもポイントです。",
    points: [
      "スランプ試験は荷卸し地点で実施",
      "「軟らかい方が楽」だが、水増しは絶対にNG（強度低下に直結）",
    ],
    glossary: [
      {
        term: "スランプ",
        meaning:
          "高さ30cmのコーンにコンクリートを詰めて引き抜いたときの下がり量（cm）。大きいほど軟らかい。",
      },
      {
        term: "ワーカビリティー",
        meaning: "打込み・締固めのしやすさと材料分離への抵抗性を合わせた性質。",
      },
      {
        term: "材料分離",
        meaning:
          "骨材とペーストが分かれてしまうこと。ジャンカや強度低下の原因になる。",
      },
    ],
    diagrams: [
      {
        svg: SVG_SLUMP,
        caption: "スランプ試験とスランプの標準値（6.2.4・6.5.2）",
      },
    ],
  },

  "6.2.5": {
    plainSummary:
      "仕上りの許容差：部材位置±20mm、柱・梁・壁・スラブ断面は0〜+20mm、基礎は0〜+50mm。打放し仕上げはA〜C種、平たんさはa〜c種（3mにつき7mm以下など）で管理する。",
    why: "コンクリートは「多少大きい分にはよいが、小さい（断面不足）のはダメ」なので、許容差がマイナス0の片側規定になっています。平たんさの等級は仕上げ厚さで決まります。薄い仕上げほど下地の凸凹が表に響くからです。",
    points: [
      "断面寸法のマイナスは原則許されない（0〜+20mm）",
      "打放しA種・B種は使うせき板の品質から違う（6.8.2と連動）",
    ],
    glossary: [
      {
        term: "不陸（ふろく/ふりく）",
        meaning: "表面の凸凹のこと。「不陸が少ない＝平らで良好」。",
      },
    ],
  },

  "6.3.1": {
    plainSummary:
      "セメントは特記がなければ普通ポルトランド等。粗骨材の最大寸法は砕石20mm・砂利25mm。水はJISの規定による。混和剤はAE剤・AE減水剤・高性能AE減水剤が基本。",
    why: "材料の急所は骨材です。粗骨材の最大寸法は鉄筋のあき（5.3.5）と連動し、大きすぎると鉄筋間を通れません。またアルカリシリカ反応（骨材とアルカリが反応して膨張しコンクリートがひび割れる現象）への対策として、骨材の区分（特記がなければA＝無害）や抑制対策が定められています。",
    glossary: [
      {
        term: "AE剤・AE減水剤",
        meaning:
          "微細な空気泡を連行して施工性と耐凍害性を高める（AE剤）、さらに水を減らせる（AE減水剤）混和剤。",
      },
      {
        term: "アルカリシリカ反応（ASR）",
        meaning:
          "骨材中のシリカとセメントのアルカリが反応・膨張し、ひび割れを起こす劣化現象。",
      },
    ],
  },

  "6.3.2": {
    plainSummary:
      "調合管理強度＝Fc＋S（構造体強度補正値）。Sは気温で3または6。空気量4.5％、水セメント比は最大65％（普通ポルトランド等）、単位水量は最大185kg/m³、単位セメント量は最小270kg/m³、塩化物は0.30kg/m³以下。",
    why: "この項は6章の心臓部です。寒い時期は強度発現が遅い（現場の構造体が不利になる）ので補正値Sが6に増えます。水セメント比が小さいほど強く耐久的ですが施工しにくい。単位水量を絞るのは乾燥収縮ひび割れを減らすためです。数値のセット（4.5%・65%・185kg・270kg・0.30kg）は試験にも頻出の基本値です。",
    points: [
      "Fc24・普通ポルトランド・平均気温8℃以上なら：調合管理強度＝24＋3＝27",
      "Ⅰ類のコンクリートは試し練りを省略できる（エコセメント・再生骨材H使用時を除く）",
    ],
    glossary: [
      {
        term: "水セメント比（W/C）",
        meaning:
          "水の質量÷セメントの質量（％）。小さいほど強く耐久的だが硬くなる。",
      },
      {
        term: "構造体強度補正値（S）",
        meaning:
          "供試体と実際の構造体の強度差を補うための上乗せ値。セメント種類と予想平均気温で3または6 N/mm²。",
      },
    ],
    diagrams: [
      {
        svg: SVG_STRENGTH,
        caption: "Fc・S・調合管理強度・呼び強度の関係（6.2.2・6.3.2・6.4.3）",
      },
    ],
  },

  "6.4.1": {
    plainSummary:
      "生コン工場は工事開始前に選定し、監督職員の承諾を受ける。時間内（6.6.2）に打込みが終わる距離にあること、同一打込み区画に2工場のコンクリートを混ぜないことが条件。",
    why: "工場が遠いと運搬時間オーバーで品質が落ちます。また工場ごとに材料・調合が微妙に違うため、同じ区画に2工場の生コンを打つと品質も責任も曖昧になります。",
    points: [
      "工場選定時に運搬時間（120分/90分ルール）をシミュレーションする",
      "大量打設で2工場使う場合も、打込み区画は分ける",
    ],
  },

  "6.4.2": {
    plainSummary:
      "生コン工場には、製造・施工・試験の指導と品質管理ができる施工管理技術者を置く。",
  },

  "6.4.3": {
    plainSummary:
      "生コンはJIS A 5308の方式で発注し、呼び強度は調合管理強度以上、保証材齢は28日。製造前に工場の配合計画書を提出し承諾を受ける。",
    why: "「呼び強度」は生コン製品の規格上の強度呼称です。現場で必要な調合管理強度以上の呼び強度を指定して発注します。配合計画書は生コンの「レシピ」で、これの承諾が品質管理のスタートラインです。",
    glossary: [
      {
        term: "呼び強度",
        meaning:
          "JIS A 5308における生コンの強度区分の呼称。発注時に指定する（例：呼び強度27）。",
      },
      {
        term: "配合計画書",
        meaning:
          "生コン工場が作成する調合の計画書。材料・調合・計算根拠を含み、承諾が必要。",
      },
    ],
  },

  "6.4.4": {
    plainSummary:
      "運搬中に水を加えない。荷卸し直前にドラムを高速回転して均質にしてから卸す。",
    points: [
      "「加水」は品質事故の典型。運転手からの申し出があっても絶対に許可しない",
    ],
  },

  "6.5.1": {
    plainSummary:
      "受入れ時は各運搬車の納入書で発注条件との適合を確認。異状を認めたコンクリートは使わない。打込み中もワーカビリティーの安定を目視で確認し続ける。",
    why: "受入れ検査は現場側の最後の砦です。納入書（出荷時刻・呼び強度・スランプなど）の確認と、目視での異状チェックを全車行います。「なんかシャバい」と感じたら止める勇気が品質を守ります。",
    points: [
      "納入書で工場名・呼び強度・スランプ・出荷時刻を毎回確認",
      "品質変化を感じたら単位水量の管理記録を工場に確認",
    ],
  },

  "6.5.2": {
    plainSummary:
      "スランプの許容差は±2.5cm（8〜18cmの場合）。外れたら調合調整や運搬改善を行うが、水セメント比は変えてはならない。",
    points: [
      "スランプ18指定なら15.5〜20.5cmが合格範囲",
      "許容差超過の生コンは受け入れない（返車）",
    ],
  },

  "6.5.3": {
    plainSummary: "空気量の許容差は±1.5％（基準4.5％なら3.0〜6.0％）。",
    why: "空気量が多すぎると強度が下がり（1％増で約4〜6％低下）、少なすぎると凍害に弱くなります。",
  },

  "6.5.4": {
    plainSummary:
      "塩化物イオン量は0.30kg/m³以下。超えたら次の運搬車から連続試験し、確認できるまで使用しない。アルカリ総量はJISの式で確認する。",
    why: "塩化物は鉄筋の錆の直接原因です。0.30kg/m³は鉄筋コンクリートの耐久性を守る基本値。現場では簡易測定器（カンタブ等）で受入れ時に測定します。",
  },

  "6.5.5": {
    plainSummary: "調合管理強度の試験・判定方法は9節（6.9.3・6.9.4）による。",
  },

  "6.6.1": {
    plainSummary:
      "現場内運搬はポンプ圧送が基本。圧送に先立ち富調合モルタルを流す（先送りモルタル）が、型枠内に打ち込んではならない。圧送中に品質変化や閉塞があった部分は廃棄。",
    why: "ポンプ配管の内面はモルタル分を最初に持っていかれるため、先にモルタルを送って管内を潤します。この先送りモルタルは調合が違うので構造体に入れてはいけません。輸送管の径は粗骨材20mmで100A以上が目安です。",
    points: [
      "先送りモルタルの処理方法（産廃 or 別用途）を打設計画で決めておく",
      "配管の支持は型枠・配筋に振動を伝えない方法で",
    ],
  },

  "6.6.2": {
    plainSummary:
      "練混ぜから打込み終了までの時間は、外気温25℃以下で120分以内、25℃超で90分以内。",
    why: "コンクリートは練った瞬間から硬化が始まります。時間が過ぎると流動性が落ち、継目（コールドジョイント）や強度低下の原因に。納入書の出荷時刻から逆算して打ち終える段取りが現場管理の腕の見せどころです。",
    points: [
      "各車の出荷時刻を控え、「あと何分」を常に把握する",
      "渋滞・ポンプ故障などの遅延リスクを打設計画で織り込む",
    ],
    diagrams: [
      {
        svg: SVG_TIME,
        caption: "練混ぜから打込み終了までの時間制限（6.6.2）",
      },
    ],
  },

  "6.6.3": {
    plainSummary:
      "打込み前に清掃・散水して湿潤に。目的の位置の近くに打ち込み、横流しはしない。柱・壁→（沈み待ち）→梁→（沈み待ち）→スラブの順。打重ねは再振動可能時間以内に。",
    why: "乾いた型枠はコンクリートの水を吸ってしまうので散水します。横流しは材料分離のもと。柱・壁を打ってすぐ梁・スラブを打つと、下のコンクリートの沈み（沈降）で梁下にひび割れが入るため、「沈みが落ち着くのを待つ」手順が定められています。",
    points: [
      "打込み前チェック：清掃・散水・スペーサー・配筋の乱れ",
      "パラペットやひさしは支持する躯体と同一区画で打つ（打継ぎを作らない）",
      "打重ね間隔が空きすぎるとコールドジョイントに（再振動可能時間以内）",
    ],
    glossary: [
      {
        term: "コールドジョイント",
        meaning:
          "先に打ったコンクリートが固まり始めてから打ち重ねてできる不連続な継目。強度・防水上の弱点。",
      },
    ],
    diagrams: [
      {
        svg: SVG_POUR_ORDER,
        caption: "打込みの順序：柱・壁→梁→スラブ（6.6.3(7)）",
      },
    ],
  },

  "6.6.4": {
    plainSummary:
      "打継ぎ位置は特記がなければ、梁・スラブはスパン中央または端から1/4付近、柱・壁はスラブ・壁梁・基礎の上端。打継ぎ面はレイタンスを除去し、水を溜めない。",
    why: "打継ぎはせん断力の小さい位置に設けるのが原則で、梁ならスパン中央付近がそれに当たります。レイタンス（表面に浮く脆弱な層）を残すと打継ぎ面の付着が悪くなり、構造・防水の弱点になります。",
    glossary: [
      {
        term: "レイタンス",
        meaning:
          "ブリーディングに伴いコンクリート表面に浮き出る脆弱な微粒子の層。打継ぎ前に除去する。",
      },
      {
        term: "ブリーディング",
        meaning: "打込み後、材料の沈降により水が表面に浮き上がる現象。",
      },
    ],
    diagrams: [
      {
        svg: SVG_JOINT,
        caption: "打継ぎの位置（6.6.4）",
      },
    ],
  },

  "6.6.5": {
    plainSummary:
      "締固めは棒形振動機が基本。各層ごとに先端が下層に入るようほぼ垂直に挿入、間隔60cm以下、表面にペーストが浮くまで加振し、穴が残らないよう加振しながら徐々に引き抜く。配管1系統につき振動機2台以上。",
    why: "振動機の目的は「余分な空気を追い出して密実にする」こと。下層に先端を入れるのは上下の層を一体化させるためです。かけすぎは材料分離を招くので「ペーストが浮くまで」が目安。予備機を含め2台以上の配置は故障対応でもあります。",
    points: [
      "「垂直に・60cm以下・下層に先端・ペーストが浮くまで・ゆっくり抜く」の5点セット",
      "型枠際・鉄筋の混んだ箇所は突き棒やたたきを併用",
    ],
    diagrams: [
      {
        svg: SVG_VIBRATOR,
        caption: "棒形振動機の使い方（6.6.5(3)）",
      },
    ],
  },

  "6.6.6": {
    plainSummary:
      "上面は荒均し後、凝結が終わる前にタンパー等でたたき締めて平たんに。壁際・柱際は木ごてでタンピング。所定の平たんさ（6.2.5）に仕上げる。",
    why: "打込み後のたたき締め（タンピング）は、沈みひび割れ・プラスチック収縮ひび割れを閉じる効果があります。「凝結が終了する前」のタイミングが重要です。",
  },

  "6.6.7": {
    plainSummary:
      "せき板を外したら豆板・空洞・コールドジョイントの有無を、支保工を外したらひび割れ・たわみを確認する。欠陥があれば6.9.6の補修手順による。",
    points: [
      "脱型後の躯体確認は写真を撮って記録に残す",
      "欠陥を勝手に補修しない（監督職員の承諾を受けた方法で）",
    ],
    glossary: [
      {
        term: "豆板（ジャンカ）",
        meaning:
          "締固め不足や材料分離により、骨材が露出して空隙だらけになった欠陥部分。",
      },
    ],
  },

  "6.7.1": {
    plainSummary:
      "打込み後5日間以上（早強は3日間以上）はコンクリート温度を2℃以上に保つ。初期凍害のおそれがある場合は寒中コンクリートの初期養生による。",
    why: "幼齢期のコンクリートが凍ると、その後どれだけ養生しても強度が戻らない「初期凍害」になります。2℃・5日間はそれを防ぐ最低ラインです。",
  },

  "6.7.2": {
    plainSummary:
      "湿潤養生の期間は、普通ポルトランド等5日以上、早強3日以上、高炉B種・中庸熱・低熱等7日以上。方法はせき板存置・シート被覆・散水・膜養生剤など。",
    why: "セメントの水和反応には水が必要です。表面が乾くと反応が止まり、表面強度不足やひび割れの原因に。「打ったら終わり」ではなく「乾かさずに育てる」のが養生です。混合セメント（B種）は反応がゆっくりなので期間が長くなります。",
    points: [
      "スラブ上面は散水や養生マットで乾燥を防ぐ",
      "せき板を早く外した面ほど乾燥しやすい＝養生を忘れずに",
    ],
    diagrams: [
      {
        svg: SVG_CURING,
        caption: "セメント種類別の湿潤養生期間（表6.7.1）",
      },
    ],
  },

  "6.7.3": {
    plainSummary:
      "硬化中のコンクリートを振動・外力から守る。打込み後少なくとも1日はその上を歩かない・作業しない。",
    points: [
      "翌日の墨出し・資材荷揚げの計画は「1日ルール」を前提に組む",
    ],
  },

  "6.8.1": {
    plainSummary:
      "型枠＝せき板＋支保工。作業荷重・自重・側圧・振動・衝撃に耐え、所定の仕上り（6.2.5）が得られ、水漏れがなく、取外し時にコンクリートを傷めないものとする。",
    glossary: [
      {
        term: "せき板",
        meaning: "コンクリートに直接接する板（合板など）。",
      },
      {
        term: "支保工",
        meaning: "せき板を支える支柱・大引・根太などの仮設構造。",
      },
      {
        term: "側圧",
        meaning:
          "打ち込んだ生コンが型枠を横に押す圧力。打込み速度が速いほど大きい。",
      },
    ],
  },

  "6.8.2": {
    plainSummary:
      "せき板の合板は特記がなければ厚12mm。コンクリート型枠用合板（JAS）を使う。締付けはボルト式。スリーブは鋼管・硬質ポリ塩化ビニル管等（柱・梁以外で径200mm以下なら紙チューブ可）。",
    points: [
      "打放し仕上げの種別（A・B種）に応じたせき板を使う",
      "はく離剤はコンクリート面に悪影響のないものを",
    ],
  },

  "6.8.3": {
    plainSummary:
      "型枠は施工図に従い組み立てる。支柱は垂直に、上下階で同一位置に。足場等の仮設物と連結しない。コーン使用箇所（防水下地・打放しA/B種・土に接する面など）が決められている。打込み前に組立状態を監督職員に報告。",
    why: "上下階の支柱位置を揃えるのは、下階のスラブに集中荷重をかけないためです。足場と型枠を繋ぐと、足場の揺れが型枠に伝わり事故や不良の原因になります。セパレーターのコーンは、頭処理後にモルタル充填できるようにするためのもので、水がかりや仕上げの薄い箇所に必要です。",
    points: [
      "支柱の下は地盤なら十分締固め＋敷板で沈下防止",
      "打込み前の型枠確認（建入れ・締付け・清掃口）を報告",
    ],
    glossary: [
      {
        term: "セパレーター・コーン",
        meaning:
          "型枠の間隔を保つ金物（セパレーター）と、その端部に付けるプラスチック製の円錐部品（コーン）。コーン跡はモルタル充填する。",
      },
    ],
  },

  "6.8.4": {
    plainSummary:
      "せき板（基礎・梁側・柱・壁）は圧縮強度5N/mm²以上で外せる。スラブ下の支柱はFcの85％以上または12N/mm²以上＋構造計算、梁下の支柱はFc以上＋構造計算。スラブ下・梁下のせき板は支柱を外した後に外す。支柱の盛替えは行わない。",
    why: "型枠を早く外したいのは工程の都合、外せないのは構造の都合。5N/mm²は「自立して表面が傷まない」レベル、支柱はスラブ・梁の自重を持てる強度が必要なので基準が厳しくなります。「盛替え禁止」は、一度荷重を受けたスラブに再度支柱を立て直す危険な行為を防ぐためです。",
    points: [
      "強度確認は現場水中養生または封かん養生供試体の圧縮強度による",
      "寒い時期は材齢ではなく圧縮強度で管理する",
      "片持梁・長大スパンは存置期間の延長を検討",
    ],
    diagrams: [
      {
        svg: SVG_FORM_PERIOD,
        caption: "型枠の最小存置期間の考え方（表6.8.2・6.8.3）",
      },
    ],
  },

  "6.8.5": {
    plainSummary:
      "型枠を外したら、仕上げのない箇所の締付け金物の頭は除去して錆止め塗料を塗る。コーン跡は保水剤または防水剤入りモルタルを充填する。",
    why: "セパレーターの金物を放置すると、そこから錆が発生してコンクリート表面に錆汁が流れます。小さな穴処理ですが、外壁の美観と耐久性を守る大事な仕事です。",
  },

  "6.9.1": {
    plainSummary:
      "この節はコンクリートの試験と構造体の仕上り確認のルール。軽易な工事は承諾を受けて試験を省略できる。",
  },

  "6.9.2": {
    plainSummary:
      "フレッシュコンクリート（固まる前）の試験は荷卸し地点で採取。スランプ・空気量・温度・塩化物量などを表6.9.1の頻度で行う。塩化物は1日1回以上＋150m³ごとに1回以上。",
    glossary: [
      {
        term: "フレッシュコンクリート",
        meaning: "練り混ぜてからまだ固まっていない状態のコンクリート。",
      },
    ],
  },

  "6.9.3": {
    plainSummary:
      "強度試験は打込み日ごと・工区ごと・150m³以下の単位ごとに実施。1回の試験は供試体3個の平均。目的別に養生方法が違う：調合管理強度→標準養生、型枠取外し→現場水中/封かん養生、構造体強度→現場養生または標準養生。",
    why: "「どの強度を知りたいか」で供試体の育て方が変わります。標準養生（20℃水中）は理想条件＝工場の実力を測る。現場養生は実際の躯体に近い条件＝構造体の強度や型枠を外せるかを判断する。目的と養生方法の対応を理解するのがこの節のキモです。",
    points: [
      "採取は「150m³以下にほぼ均等分割した単位ごと」＋打込み日ごと・工区ごと",
      "構造体強度判定用は3台の運搬車から1個ずつ（計3個）",
    ],
    glossary: [
      {
        term: "供試体（テストピース）",
        meaning:
          "強度試験用の円柱形コンクリート試料（φ100×200mmなど）。現場で採取して作る。",
      },
      {
        term: "標準養生",
        meaning: "20±2℃の水中で養生する方法。強度発現の標準条件。",
      },
      {
        term: "封かん養生",
        meaning: "水分の出入りを防いで密封し、現場の温度で養生する方法。",
      },
    ],
  },

  "6.9.4": {
    plainSummary:
      "調合管理強度の判定は3回の試験で行い、①各回の平均が調合管理強度の85％以上、かつ②3回の総平均が調合管理強度以上なら合格。",
    why: "1回だけの結果で合否を決めず、ばらつきを考慮して「個々は85％まで許すが、平均はフルで満たせ」という二段構えの判定です。不合格なら原因を調査し、措置について監督職員の承諾が必要です。",
    diagrams: [
      {
        svg: SVG_STRENGTH_TEST,
        caption: "調合管理強度の判定（6.9.4）",
      },
    ],
  },

  "6.9.5": {
    plainSummary:
      "構造体コンクリート強度は3ルートのいずれかで判定：A)現場水中養生28日（20℃以上→調合管理強度以上／20℃未満→Fc+3以上） B)現場封かん養生（28日≧0.7Fc かつ 91日以内≧Fc+3） C)標準養生28日≧調合管理強度。不合格ならコア採取等で確認。",
    why: "「実際の建物のコンクリートがFcを満たしているか」を確かめる最終チェックです。どのルートで判定するかは事前に決めておきます。不合格＝即アウトではなく、コア（構造体から直接くり抜いた供試体）で実力を確認する道が用意されています。",
    glossary: [
      {
        term: "コア供試体",
        meaning:
          "硬化した構造体からコアドリルでくり抜いた円柱試料。構造体の実強度を直接確認できる。",
      },
    ],
    diagrams: [
      {
        svg: SVG_STRUCT_TEST,
        caption: "構造体コンクリート強度の判定3ルート（6.9.5）",
      },
    ],
  },

  "6.9.6": {
    plainSummary:
      "位置・断面寸法は測定可能になったら直ちに確認。仕上り状態・欠陥部・ひび割れ、かぶり厚さ不足の兆候を確認して監督職員に報告。適合しない場合は承諾を受けた方法で補修し、補修後直ちに検査を受ける。",
    points: [
      "「確認→報告→承諾→補修→検査」の順番を崩さない（勝手に直さない）",
      "かぶり不足の兆候：表面に鉄筋の影・錆汁が見える等",
    ],
  },

  "6.10.1": {
    plainSummary:
      "この節は人工軽量骨材を使う軽量コンクリートのルール。適用箇所は特記による。",
  },

  "6.10.2": {
    plainSummary:
      "軽量コンクリートは1種（粗骨材のみ軽量、1.8〜2.1t/m³）と2種（細骨材も軽量、1.4〜1.8t/m³）。スランプは特記がなければ21cm。",
  },

  "6.10.3": {
    plainSummary:
      "人工軽量骨材は最大寸法15mm、あらかじめ十分吸水させてから使う。空気量5.0％、水セメント比最大55％、単位セメント量最小320kg/m³。",
    why: "軽量骨材は内部に空隙があり水を吸います。乾いたまま使うとポンプ圧送中に圧力で吸水（プレウェッティング不足）してスランプが激減するため、事前吸水が必須です。",
  },

  "6.10.4": {
    plainSummary:
      "圧送距離が長い場合は輸送管を太く（水平換算150m以上で125A以上）。表面に浮いた軽量骨材はタンピングで押さえ込んで平たんにする。",
    why: "軽量骨材は文字どおり軽いので、締固めで浮いてきます。浮いた骨材を押さえ込むひと手間が仕上がりを左右します。",
  },

  "6.10.5": {
    plainSummary:
      "単位容積質量試験を行い、計画調合に基づく基準値との差が±3.5％以内なら合格。",
  },

  "6.11.1": {
    plainSummary:
      "打込み後の養生期間中に凍結のおそれがある期間のコンクリートは「寒中コンクリート」として施工する。適用期間は特記による。養生・保温の計画を施工計画書に定める。",
  },

  "6.11.2": {
    plainSummary:
      "骨材は凍っていないものを使う。水セメント比の最大値は60％。構造体強度補正値Sは予想平均気温（または計画養生温度）により定める。",
  },

  "6.11.3": {
    plainSummary:
      "荷卸し時のコンクリート温度は10℃以上20℃未満。材料を加熱する場合、セメントは加熱禁止、骨材は直接火にかけない。凍結した地盤の上に打込み・支柱立てをしない。",
    why: "コンクリート温度が低すぎると凍害、高すぎると急硬化・ひび割れのリスク。だから10〜20℃のレンジ指定です。凍った地盤は融けたときに沈下し、支柱や打設物を狂わせます。",
    points: [
      "型枠内・鉄筋の氷雪は打込み前に必ず除去",
      "加熱するのは水（または骨材を間接的に）。セメントは絶対に加熱しない",
    ],
  },

  "6.11.4": {
    plainSummary:
      "初期養生は圧縮強度5N/mm²以上になるまで、全部分を2℃以上に保つ。気温0℃以下が予想されるときは開口部も覆い、−10℃以下ならば構造物全体を覆って採暖する。温度は自記記録温度計で記録。",
    why: "5N/mm²は「凍っても壊れない」目安の強度です。そこまで育てば初期凍害の心配がなくなります。保温空間の温度・コンクリート温度を記録し続けることが、養生打切りの根拠になります。",
    glossary: [
      {
        term: "初期凍害",
        meaning:
          "硬化初期のコンクリートが凍結して受ける損傷。後から回復しない。",
      },
      {
        term: "採暖",
        meaning: "ヒーター等で空間を暖めること。急加熱・急乾燥はNG。",
      },
    ],
  },

  "6.11.5": {
    plainSummary:
      "寒中コンクリートの型枠取外し時期は、材齢ではなく圧縮強度により決定する。",
  },

  "6.11.6": {
    plainSummary:
      "寒中の試験は表6.11.1による。構造体強度判定は封かん養生供試体で、28日≧0.7Fc かつ 91日以内≧Fc+3。供試体が凍っているおそれがあれば約10℃の水に2〜6時間浸けてから試験する。",
  },

  "6.12.1": {
    plainSummary:
      "日平均気温の日別平滑値が25℃を超える期間は「暑中コンクリート」として施工する。",
  },

  "6.12.2": {
    plainSummary:
      "必要に応じてAE減水剤遅延形等を使う。スランプは特記がなければ21cm。構造体強度補正値Sは特記がなければ3（低熱ポルトランドは0）。",
    why: "暑いとスランプの低下が速いので、標準より大きめの21cmが基本値になっています。遅延形混和剤は凝結を遅らせて打重ね時間を稼ぐ道具です。",
  },

  "6.12.3": {
    plainSummary:
      "荷卸し時のコンクリート温度は35℃以下。練混ぜから打込み終了まで90分以内。せき板・打継ぎ面へ十分散水し、輸送管は濡れシートで覆う。コールドジョイントの防止を計画的に。",
    points: [
      "夏場は「35℃・90分」が合言葉",
      "打設順序・人員・ポンプ台数を増やして打重ね間隔を詰める",
    ],
  },

  "6.12.4": {
    plainSummary:
      "暑中の養生は、表面への散水で常に湿潤に保つ。上面はブリーディング水が消えた時点から、せき板面は脱型直後から湿潤養生を始める。",
    why: "夏は水分が急速に飛び、プラスチック収縮ひび割れが出やすい季節です。「ブリーディング水消失＝乾燥開始のサイン」を逃さず散水を始めます。",
  },

  "6.13.1": {
    plainSummary:
      "部材断面が大きく、水和熱の温度上昇で有害なひび割れのおそれがある部分は「マスコンクリート」として施工する。適用箇所は特記による。",
    glossary: [
      {
        term: "マスコンクリート",
        meaning:
          "大断面でセメントの水和熱がこもりやすいコンクリート（厚い基礎・地中梁など）。温度ひび割れ対策が必要。",
      },
      {
        term: "水和熱",
        meaning: "セメントと水の反応で発生する熱。大断面では内部温度が大きく上がる。",
      },
    ],
  },

  "6.13.2": {
    plainSummary:
      "セメントは中庸熱・低熱・高炉B種など発熱の小さいものから特記で選ぶ。単位セメント量は可能な限り少なく（最小値270kg/m³は適用しない）。スランプは特記がなければ15cm。",
    why: "温度ひび割れの原因はセメントの発熱なので、「発熱の少ないセメントを、少なく使う」のが対策の基本です。",
  },

  "6.13.3": {
    plainSummary: "荷卸し時のコンクリート温度は35℃以下。",
  },

  "6.13.4": {
    plainSummary:
      "内部温度上昇中は表面が急冷しないよう養生し、最高温度後は内外温度差と降下速度を小さく保つ。せき板は表面と外気の温度差が小さくなってから外す。",
    why: "温度ひび割れは「内は熱い・外は冷たい」の温度差で起こります。だから冷やすのではなく、むしろ表面を保温して内外の差を縮めるのが正解です。型枠を早く外すと表面が急冷されるので逆効果になります。",
  },

  "6.13.5": {
    plainSummary:
      "マスコンクリートの構造体強度判定用供試体は標準養生とし、材齢28日で調合管理強度以上なら合格。",
  },

  "6.14.1": {
    plainSummary:
      "捨コンクリートなど補強筋を必要としないコンクリートが「無筋コンクリート」。特記がなければFc18・スランプ15または18cm。適用箇所は捨コン・防水保護コン・縁石・舗装コンなど。",
    glossary: [
      {
        term: "捨コンクリート",
        meaning:
          "基礎下に打つ均しコンクリート。墨出しと作業面の確保が目的で構造体ではない。",
      },
    ],
  },

  "6.14.2": {
    plainSummary:
      "粗骨材最大寸法は断面最小寸法の1/4以下（捨コン・防水保護コンは25mm以下）。構造体強度補正値S・水セメント比の最大値・単位セメント量の最小値は適用しない。",
    why: "構造耐力を負担しないため、耐久性関係の縛りが緩和されています。ただし強度試験（6.14.3）は行います。",
  },

  "6.14.3": {
    plainSummary:
      "調合管理強度の試験・判定は6.9.3・6.9.4に準じる。Ⅰ類なら構造体コンクリート強度試験は省略できる。",
  },

  "6.15.1": {
    plainSummary:
      "工事現場で普通コンクリート（ベースコンクリート）に流動化剤を添加・かくはんして軟らかくするのが「流動化コンクリート」。適用は特記による。",
    why: "水を増やさずに（＝強度を落とさずに）流動性だけを高める技術です。ベースのスランプ15以下→流動化後21以下が標準の組合せです。",
  },

  "6.15.2": {
    plainSummary:
      "流動化剤はJIS A 6204による。ベースコンクリートのスランプ15cm以下→流動化後21cm以下（表6.15.1）。空気量は特記がなければ4.5％。",
  },

  "6.15.3": {
    plainSummary:
      "流動化剤は原液のまま、所定量を一度に添加。計量誤差は±3％以内。品質管理担当者を配置し、スランプフローで管理する場合は材料分離がないことを確認する。",
  },

  "6.15.4": {
    plainSummary:
      "練混ぜから打込み終了までの時間は6.6.2の範囲内で、練混ぜから流動化までを可能な限り短く。流動性の低下を考慮して打重ね間隔も短く定める。",
  },

  "6.16.1": {
    plainSummary:
      "工場で流動性を著しく高めて出荷するのが「高流動コンクリート」。締固めがほとんど不要で、過密配筋や複雑な形状に使う。適用箇所は特記による。",
    glossary: [
      {
        term: "高流動コンクリート",
        meaning:
          "材料分離への抵抗性を保ったまま流動性を高めたコンクリート。スランプフローで管理する。",
      },
      {
        term: "スランプフロー",
        meaning:
          "スランプ試験後に広がったコンクリートの直径（cm）。流動性の高いコンクリートの指標。",
      },
    ],
  },

  "6.16.2": {
    plainSummary:
      "スランプフローは特記による（45〜60cmの範囲）。空気量は特記がなければ4.5％。水セメント比の上限を定めて承諾を得る。",
  },

  "6.16.3": {
    plainSummary:
      "工場はスランプフロー45〜60cmの製品認証を受けた工場等に限る。過度の締固めは材料分離を招くため禁止。仕切材はペーストが流出しないものを使う。",
    why: "高流動コンクリートは自己充填性が持ち味で、通常の感覚で振動機をかけすぎるとかえって分離します。「締固めの要否を確認してから」が原則です。",
  },

  "6.16.4": {
    plainSummary:
      "スランプ試験の代わりにスランプフロー試験（JIS A 1150）を行う。構造体強度の判定は6.9.5による。",
  },
};

export default ch06;
